import { APPLICANTS_STATUS_CODE, COUNTRY } from "@/src/constants/Common";
import applicantsActions from "@/src/redux/applicants/actions";
import { FETCH_APPLICANT_DETAILS_FAILURE, FETCH_APPLICANT_DETAILS_SUCCESS } from "@/src/redux/applicants/actionTypes";
import { getApplicantsSelector } from "@/src/redux/applicants/selectors";
import systemCodesActions from "@/src/redux/system-codes/actions";
import { getCitiesSystemCodesSelector } from "@/src/redux/system-codes/selectors";
import { IApplicantsResponse } from "@/src/types/applicants";
import { Details } from "@/src/types/application";
import { isCountryUnderAmerica } from "@/src/utils/form";
import { getCategoryKeysByCity } from "@/src/utils/string";
import { getStartDate } from "@/src/utils/date";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

type UserApplicationDetailsHook = {
	applicationDetails?: Details,
	error: boolean
}

export const useUserApplicationDetails = (): UserApplicationDetailsHook => {
	const [applicationDetails, setApplicationDetails] = useState<Details>();
	const [fetching, setFetching] = useState<boolean>(false);
	const [error, setError] = useState<boolean>(false);
	const dispatch = useDispatch();
	const router = useRouter();
	const applicationSelector = useSelector(getApplicantsSelector);

	useEffect(() => {
		// TODO: Make efficient by caching, add support in BE. Commented out since the data can become stale after updating in admin side.
		//if (router.isReady && applicationSelector.details && applicationDetails !== applicationSelector.details && applicationSelector.details.id === router.query.id) {
		//	setApplicationDetails(applicationSelector.details);
		//}
		//else if (router.isReady) {
		//	dispatch(applicantsActions.fetchApplicantDetails(router.query.id as string));
		//	dispatch(systemCodesActions.fetchExtendedStayOptions()); // Load the system codes in redux.
		//}
		if (router.isReady) {
			setFetching(true);
			dispatch(applicantsActions.fetchApplicantDetails(router.query.id as string));
			dispatch(systemCodesActions.fetchExtendedStayOptions()); // Load the system codes in redux.
			dispatch(systemCodesActions.fetchRoomCapacity()); // Load the system codes in redux.
			dispatch(systemCodesActions.fetchVisaApplication())
		}
	}, [router.query.id]);

	useEffect(() => {
		switch (applicationSelector.action) {
			case FETCH_APPLICANT_DETAILS_SUCCESS:
				if (fetching) {
					setFetching(false);
					setApplicationDetails(applicationSelector.details);
					setError(false);
				}
				break;
			case FETCH_APPLICANT_DETAILS_FAILURE:
				if (fetching) {
					setFetching(false);
					setApplicationDetails(undefined);
					setError(true);
				}
				break
		}
	}, [applicationSelector]);

	return { applicationDetails, error };
}

export const useApplicantDetailsById = (): IApplicantsResponse | undefined => {
	const [applicantDetails, setApplicantDetails] = useState<IApplicantsResponse | undefined>(undefined);
	const applicantSelector = useSelector(getApplicantsSelector);
	const dispatch = useDispatch();
	const router = useRouter();

	useEffect(() => {
		if (router.query.id && applicantSelector.applicationsByPhoneNum) {
			const currentApplication: IApplicantsResponse | undefined = applicantSelector
				.applicationsByPhoneNum
				?.find((application: IApplicantsResponse) => application.id === router.query.id as string);
			setApplicantDetails(currentApplication);

			if (currentApplication?.countrySysCode) {
        if (isCountryUnderAmerica(currentApplication.countrySysCode.key)) {
          dispatch(systemCodesActions.fetchMultipleCityOptions(getCategoryKeysByCity([COUNTRY.US_EAST, COUNTRY.US_WEST, COUNTRY.HAWAII])));
        } else {
          dispatch(systemCodesActions.fetchCityOptions(currentApplication.countrySysCode.key));
        }
			}
			if (currentApplication?.citySysCode) {
				dispatch(systemCodesActions.fetchSchoolOptions(currentApplication.citySysCode.key));
			}
		}
	}, [applicantSelector.applicationsByPhoneNum, router.query.id]);

	return applicantDetails;
}

export const useApplicantDetails = (): IApplicantsResponse | undefined => {
	const applicantSelector = useSelector(getApplicantsSelector);

	return useMemo(() => {
		const applications = applicantSelector.applicationsByPhoneNum ?? [];
		const waitingApplications = applications.filter(
			(application: IApplicantsResponse) =>
				application.applicationStatus === APPLICANTS_STATUS_CODE.WAITING_FOR_FORM_RESPONSE
		);

		const now = new Date();
		now.setHours(0, 0, 0, 0);

		const waitingWithDate = waitingApplications
			.map((application: any) => ({ application, date: getStartDate(application) }))
			.filter(({ date }: any) => date !== null) as { application: IApplicantsResponse; date: Date }[];

		const waitingWithoutDate = waitingApplications.filter(
			(application: any) => getStartDate(application) === null
		);

		if (waitingWithDate.length > 0) {
			const nearest = waitingWithDate.reduce((prev, current) => {
				const prevDiff = Math.abs(prev.date.getTime() - now.getTime());
				const currentDiff = Math.abs(current.date.getTime() - now.getTime());
				return currentDiff < prevDiff ? current : prev;
			});
			return nearest.application;
		}
		if (waitingWithoutDate.length > 0) {
			return waitingWithoutDate[0];
		}
		return undefined;
	}, [applicantSelector.applicationsByPhoneNum]);
};
